import React from "react";
import { Box, IconButton, Slider, Stack, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { palette } from "../../styles/theme";
import { OutputImage, TRANSFORMATION_LABELS } from "./types";

interface SpecsImageAdjustmentRowProps {
  transformation: OutputImage["transformations"][number];
  onRemove: () => void;
  onUpdate: (
    patch: Partial<{
      enabled: boolean;
      params: Record<string, number | string>;
    }>
  ) => void;
}

export function SpecsImageAdjustmentRow({
  transformation,
  onRemove,
  onUpdate,
}: SpecsImageAdjustmentRowProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        mb: 0.5,
        bgcolor: palette.grey.lightBackground,
        borderRadius: "4px",
      }}
    >
      <Typography variant="body2" sx={{ minWidth: 0 }}>
        {TRANSFORMATION_LABELS[transformation.type]}
      </Typography>

      {transformation.type === "adjustContrast" && (
        <Slider
          value={(transformation.params.contrast as number) ?? 0}
          min={-100}
          max={100}
          size="small"
          onChange={(_, value) =>
            onUpdate({
              params: { ...transformation.params, contrast: value as number },
            })
          }
          sx={{ flex: 1, mx: 1 }}
        />
      )}
      {transformation.type === "adjustBrightness" && (
        <Slider
          value={(transformation.params.brightness as number) ?? 0}
          min={-100}
          max={100}
          size="small"
          onChange={(_, value) =>
            onUpdate({
              params: { ...transformation.params, brightness: value as number },
            })
          }
          sx={{ flex: 1, mx: 1 }}
        />
      )}
      {transformation.type === "adjustSaturation" && (
        <Slider
          value={(transformation.params.saturation as number) ?? 0}
          min={-100}
          max={100}
          size="small"
          onChange={(_, value) =>
            onUpdate({
              params: { ...transformation.params, saturation: value as number },
            })
          }
          sx={{ flex: 1, mx: 1 }}
        />
      )}
      {transformation.type === "sharpen" && (
        <Stack direction="row" spacing={1} sx={{ flex: 1, mx: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption">Radius</Typography>
            <Slider
              value={(transformation.params.radius as number) ?? 20}
              min={1}
              max={200}
              size="small"
              onChange={(_, value) =>
                onUpdate({
                  params: { ...transformation.params, radius: value as number },
                })
              }
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption">Strength</Typography>
            <Slider
              value={(transformation.params.strength as number) ?? 2}
              min={0}
              max={20}
              step={0.1}
              size="small"
              onChange={(_, value) =>
                onUpdate({
                  params: {
                    ...transformation.params,
                    strength: value as number,
                  },
                })
              }
            />
          </Box>
        </Stack>
      )}
      <IconButton size="small" onClick={onRemove}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
